import React, { useEffect, useState } from "react";
import { Phone, Calendar, ShieldCheck, Award, Car } from "lucide-react";
import { PHONE, PHONE_TEL, HERO_SLIDES } from "../lib/assets";
import { useLang } from "../context/LangContext";

const Hero = () => {
  const { t } = useLang();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, 3000);
    return () => clearInterval(id);
    // HERO_SLIDES is a module-level constant; interval runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 pb-20 sm:pb-28 overflow-hidden grain-bg"
    >
      {/* Decorative blobs */}
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full bg-brand-navy/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-navy/5 border border-brand-navy/10 mb-6">
            <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse" />
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-brand-navy">
              {t.hero.eyebrow}
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-brand-navy leading-[1.05] tracking-tight">
            {t.hero.title1}{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-brand-navy">{t.hero.title2}</span>
              <span className="absolute left-0 bottom-1 w-full h-3 bg-brand-orange/40 -z-0 rounded-sm" />
            </span>
          </h1>

          <p className="mt-6 text-base sm:text-lg lg:text-xl text-brand-muted max-w-2xl leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={() => scrollTo("contact")}
              data-testid="hero-booking-btn"
              className="inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orange-deep text-white font-semibold px-7 py-4 rounded-full transition-all hover:-translate-y-0.5 shadow-md hover:shadow-xl"
            >
              <Calendar className="w-5 h-5" />
              {t.hero.bookNow}
            </button>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold px-7 py-4 rounded-full transition-all"
            >
              <Phone className="w-5 h-5" />
              {t.hero.callNow}
            </a>
          </div>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-xl">
            <Stat icon={<Award className="w-5 h-5" />} label={t.hero.stat1Label} value={t.hero.stat1Value} />
            <Stat icon={<ShieldCheck className="w-5 h-5" />} label={t.hero.stat2Label} value={t.hero.stat2Value} />
            <Stat icon={<Car className="w-5 h-5" />} label={t.hero.stat3Label} value={t.hero.stat3Value} />
          </div>
        </div>

        <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.2s" }}>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-brand-orange/30 to-brand-navy/30 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden border-4 border-white shadow-2xl" data-testid="hero-slideshow">
              <div className="relative w-full h-[420px] sm:h-[480px]">
                {HERO_SLIDES.map((slide, i) => (
                  <img
                    key={slide.src}
                    src={slide.src}
                    alt={`${slide.title} - BIU BIU DRIVING`}
                    data-testid={`hero-slide-${i}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      i === index ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-brand-navy-deep/95 via-brand-navy-deep/60 to-transparent p-6">
                <div className="flex items-center justify-between text-white gap-3">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.2em] text-brand-orange font-bold truncate">
                      {HERO_SLIDES[index].plate}
                    </p>
                    <p className="font-display font-bold text-lg mt-0.5 truncate">
                      {HERO_SLIDES[index].title}
                    </p>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/80 font-semibold mt-0.5">
                      {HERO_SLIDES[index].tag}
                    </p>
                  </div>
                  <div className="px-3 py-1.5 rounded-full bg-brand-orange text-white text-xs font-bold uppercase tracking-wider flex-shrink-0">
                    {HERO_SLIDES[index].sub}
                  </div>
                </div>
              </div>

              {/* Slideshow indicators */}
              <div className="absolute top-4 right-4 flex gap-1.5">
                {HERO_SLIDES.map((slide, i) => (
                  <button
                    key={slide.src}
                    onClick={() => setIndex(i)}
                    aria-label={`Slide ${i + 1}`}
                    data-testid={`hero-indicator-${i}`}
                    className={`h-1.5 rounded-full transition-all ${
                      i === index ? "w-8 bg-brand-orange" : "w-3 bg-white/50 hover:bg-white/80"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-2 text-brand-orange">{icon}</div>
    <div className="font-display text-xl sm:text-2xl lg:text-3xl font-extrabold text-brand-navy leading-tight">{value}</div>
    <div className="text-xs uppercase tracking-wider text-brand-muted font-semibold">{label}</div>
  </div>
);

export default Hero;
